import {
  getEntitiesByKeyList,
  getCascadingFields,
} from "@shared/components/form-builder/utils";

const findRelatedFieldsInForms = (formField, restForms) => {
  const relatedFields = [];

  restForms.forEach((form) => {
    form?.form_data.sections.forEach((section) => {
      const relatedFieldsMatches = getEntitiesByKeyList(section?.entities, {
        field_id: formField.field_id,
        field_fe_id: formField.field_fe_id,
      });
      relatedFields.push(...relatedFieldsMatches);
    });
  });

  return relatedFields;
}

const getRelatedMultiBlockList = (blockData, entityList) => {
  // get list of unique objects to avoid duplicates in case of linked blocks:
  const multiBlockList = entityList
    .filter((el) => el.block_id === blockData.block_id)
    .filter((item, index, self) =>
      index === self.findIndex((t) => t.block_fe_id === item.block_fe_id)
    );
  return multiBlockList;
}

const getRelatedMultiBlockFields = (fieldData, parentEntity, parentList, restForms) => {
  const relatedFields = [];

  const multiBlockList = getRelatedMultiBlockList(parentEntity, parentList);
  const currentMultiBlockIndex = multiBlockList.findIndex((item) => item.block_fe_id === parentEntity.block_fe_id);

  restForms.forEach((form) => {
    const formSections = form?.form_data?.sections || [];
    const formRelatedMultiBlockList = getEntitiesByKeyList(
      formSections, {
        block_id: parentEntity.block_id,
      })
      .filter((item, index, self) =>
        index === self.findIndex((t) => t.block_fe_id === item.block_fe_id)
      );

    const relatedMultiBlock = formRelatedMultiBlockList[currentMultiBlockIndex];

    const relatedFieldsMatches = getEntitiesByKeyList(relatedMultiBlock?.entities, {
      field_id: fieldData.field_id,
      field_fe_id: fieldData.field_fe_id,
    });
    relatedFields.push(...relatedFieldsMatches);
  });

  return relatedFields;
}

const isValuesEqual = (formEntity, field) => {
  switch (formEntity?.type) {
    case 'MULTI_SELECT':
      return field.option_value == formEntity.option_value;
    case 'CHECKBOX':
      return !!field.value?.every(x => x?.option_value == field.value?.find(x2 => x.id === x2.id)?.option_value);
    default:
      return field.value == formEntity.value;
  }
}

/**
 * Organizes the section comparison list.
 * @param {Object[]} list list of entities
 * @param {Object[]} restForms list of forms to compare against
 * @param {Object} options options for organizing
 * @param {Object} options.entity parent entity for the current entity
 * @param {Object} options.list list of entities for the current entity
 * @returns 
 */
const organizeSectionCompareList = (list, restForms, { entity: parentEntity, list: parentList } = {}) => {
  return list.map((formEntity) => {
    if (formEntity.entities) {
      return {
        ...formEntity,
        entities: organizeSectionCompareList(formEntity.entities, restForms, { entity: formEntity, list }),
      }
    } else if (formEntity.form_entity_type === "FIELD") {
      let relatedFields;
      if (!parentEntity?.is_block_multi) {
        relatedFields = findRelatedFieldsInForms(formEntity, restForms);
      } else {
        relatedFields = getRelatedMultiBlockFields(formEntity, parentEntity, parentList, restForms);
      }

      const hasDifferentValues = relatedFields.some((field) => {
        return !isValuesEqual(formEntity, field);
      });

      const result = {
        ...formEntity,
        highlight: hasDifferentValues,
      };

      const cascadingFields = getCascadingFields(formEntity);
      if (cascadingFields) {
        result.options = result.options.map((option) => {
          return {
            ...option,
            cascading_fields: organizeSectionCompareList(option.cascading_fields, restForms),
          }
        })
      }

      return result;
    }
  });
}

/**
 * Organizes the comparison data by extracting relevant fields.
 * Checks if related fields have different values.
 * @param {Array} formList List of section lists
 */
export const organizeComparisonData = (formList) => {

  const result = formList.map((formItem) => {
    const restLists = formList.filter((item) => item.id !== formItem.id);

    return {
      ...formItem,
      form_data: {
        ...formItem.form_data,
        sections: formItem.form_data.sections.map((section) => {
          return {
            ...section,
            entities: organizeSectionCompareList(section.entities, restLists, { entity: section, list: formItem.form_data.sections }),
          };
        }),
      },
    };
  });

  return result;
}